import { z } from "zod";
import { BASE_UNITS, UNIT_CODES } from "../units";
import { decimal, money, slug, trimmed, uuid } from "./common";

const pct = z.coerce.number().min(0, { error: "От 0 до 99.99" }).max(99.99, { error: "От 0 до 99.99" });
const optionalDecimal = decimal.optional().nullable();
export const CHANNELS = ["pos", "qr", "website", "delivery"] as const;
export const ALLERGENS = ["gluten", "lactose", "eggs", "nuts", "peanuts", "soy", "fish", "shellfish", "sesame", "celery", "mustard", "sulfites"] as const;

// ───────── Продукты ─────────
export const PRODUCT_TYPES = ["ingredient", "goods", "semi_finished", "packaging", "consumable"] as const;

export const productSchema = z.strictObject({
  name: trimmed(2, 160),
  type: z.enum(PRODUCT_TYPES),
  categoryId: uuid.nullable().optional(),
  sku: z.string().trim().regex(/^[A-Za-z0-9._-]{1,64}$/, { error: "Латиница, цифры, . _ - (до 64)" }).nullable().optional(),
  barcodes: z.array(z.string().trim().regex(/^\d{6,14}$/, { error: "Штрихкод — 6–14 цифр" })).max(20).default([]),
  baseUnit: z.enum(BASE_UNITS),
  purchaseUnit: z.enum(UNIT_CODES as [string, ...string[]]).nullable().optional(),
  purchaseUnitFactor: optionalDecimal,
  minStock: optionalDecimal,
  shelfLifeDays: z.number().int().min(1).max(3650).nullable().optional(),
  /** Цена закупки за базовую единицу, minor units (может быть дробной: 0.08 тыйын/г) */
  lastPurchasePrice: z.string().regex(/^\d{1,12}(\.\d{1,6})?$/, { error: "Некорректная цена" }).nullable().optional(),
  wastePercent: pct.default(0),
  /** Потери при закупке по умолчанию: percent — % (меньше 100), qty — фикс в кг/л/шт (крупная единица продукта) с закупки; null — нет */
  purchaseLossMode: z.enum(["percent", "qty"]).nullable().optional(),
  purchaseLossValue: z.coerce.number().min(0, { error: "Не меньше нуля" }).max(1e6).default(0),
  density: z.string().regex(/^\d{1,4}(\.\d{1,4})?$/).nullable().optional(),
  allergens: z.array(z.enum(ALLERGENS)).default([]),
  isActive: z.boolean().default(true),
});
export const productUpdateSchema = productSchema.partial().extend({ version: z.number().int().min(1) });
export type ProductInput = z.input<typeof productSchema>;

export const productsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  q: z.string().trim().max(100).optional(),
  type: z.enum(PRODUCT_TYPES).optional(),
});

// ───────── Меню ─────────
export const menuCategorySchema = z.strictObject({
  name: trimmed(1, 80),
  slug,
  parentId: uuid.nullable().optional(),
  icon: z.string().max(40).nullable().optional(),
  imageFileId: uuid.nullable().optional(),
  isVisible: z.boolean().default(true),
  visibleChannels: z.array(z.enum(CHANNELS)).default([...CHANNELS]),
});
export const menuCategoryUpdateSchema = menuCategorySchema.partial().extend({ version: z.number().int().min(1) });

export const reorderSchema = z.strictObject({ ids: z.array(uuid).min(1).max(1000) });

export const MENU_ITEM_TYPES = ["dish", "drink", "product", "service", "combo", "modifier_only"] as const;
export const TAGS = ["new", "hit", "spicy", "vegan", "vegetarian", "halal"] as const;

const variant = z.strictObject({ id: uuid.optional(), name: trimmed(1, 60), price: money, sku: z.string().trim().max(64).nullable().optional(), isDefault: z.boolean().default(false) });

export const menuItemSchema = z
  .strictObject({
    categoryId: uuid,
    type: z.enum(MENU_ITEM_TYPES).default("dish"),
    name: trimmed(1, 120),
    slug,
    description: trimmed(0, 2000).nullable().optional(),
    sku: z.string().trim().regex(/^[A-Za-z0-9._-]{1,64}$/).nullable().optional(),
    basePrice: money,
    oldPrice: money.nullable().optional(),
    weightG: optionalDecimal,
    volumeMl: optionalDecimal,
    calories: optionalDecimal,
    allergens: z.array(z.enum(ALLERGENS)).default([]),
    tags: z.array(z.enum(TAGS)).default([]),
    /** Теги кассы (MenuTag) */
    tagIds: z.array(uuid).max(20).default([]),
    /** Штрихкоды для сканера кассы: EAN-8/13, UPC, свой код */
    barcodes: z.array(z.string().trim().regex(/^[0-9A-Za-z-]{4,32}$/, { error: "Штрихкод: 4–32 символа" })).max(10).default([]),
    cookingTimeMin: z.number().int().min(0).max(600).nullable().optional(),
    isWeighted: z.boolean().default(false),
    productId: uuid.nullable().optional(),
    imageFileId: uuid.nullable().optional(),
    /** Тип станции кухни (BR-029); null — станция филиала по умолчанию */
    kitchenStationType: z.enum(["hot", "cold", "bar", "coffee", "bakery", "pizza", "grill", "dessert", "packing", "expo"]).nullable().optional(),
    isActive: z.boolean().default(true),
    isVisiblePublic: z.boolean().default(true),
    allowDiscount: z.boolean().default(true),
    minAge: z.number().int().min(0).max(21).nullable().optional(),
    variants: z.array(variant).max(20).default([]),
    modifierGroupIds: z.array(uuid).max(30).default([]),
  })
  .refine((v) => v.oldPrice == null || v.oldPrice > v.basePrice, { error: "Старая цена должна быть больше текущей", path: ["oldPrice"] })
  .refine((v) => v.type !== "product" || !!v.productId, { error: "Для товара выберите складской продукт", path: ["productId"] })
  .refine((v) => v.variants.filter((x) => x.isDefault).length <= 1, { error: "Вариант по умолчанию — только один", path: ["variants"] })
  .refine((v) => new Set(v.variants.map((x) => x.name.toLowerCase())).size === v.variants.length, { error: "Названия вариантов повторяются", path: ["variants"] })
  .refine((v) => new Set(v.barcodes).size === v.barcodes.length, { error: "Штрихкоды повторяются", path: ["barcodes"] });
export const menuItemUpdateSchema = z.intersection(menuItemSchema, z.object({ version: z.number().int().min(1) }));
export type MenuItemInput = z.input<typeof menuItemSchema>;

export const menuItemsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  q: z.string().trim().max(100).optional(),
  categoryId: uuid.optional(),
  active: z.enum(["true", "false"]).optional(),
});

const modifier = z.strictObject({
  id: uuid.optional(),
  name: trimmed(1, 80),
  price: z.number().int().min(-1e9).max(1e13),
  productId: uuid.nullable().optional(),
  quantity: optionalDecimal,
  replacesProductId: uuid.nullable().optional(),
  removesProductId: uuid.nullable().optional(),
  kitchenLabel: trimmed(0, 60).nullable().optional(),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export const modifierGroupSchema = z
  .strictObject({
    name: trimmed(1, 80),
    minSelect: z.number().int().min(0).max(50).default(0),
    maxSelect: z.number().int().min(1).max(50).nullable().optional(),
    selectionType: z.enum(["single", "multiple"]).default("multiple"),
    allowQuantity: z.boolean().default(false),
    maxQuantityPerModifier: z.number().int().min(1).max(20).nullable().optional(),
    freeQuantity: z.number().int().min(0).max(50).default(0),
    modifiers: z.array(modifier).min(1, { error: "Добавьте хотя бы один модификатор" }).max(100),
  })
  .refine((g) => g.maxSelect == null || g.maxSelect >= g.minSelect, { error: "Максимум не меньше минимума", path: ["maxSelect"] })
  .refine((g) => g.selectionType !== "single" || (g.maxSelect ?? 1) === 1, { error: "Для одиночного выбора максимум = 1", path: ["maxSelect"] })
  .refine((g) => g.minSelect <= g.modifiers.filter((m) => m.isActive).length, { error: "Минимум больше числа активных модификаторов", path: ["minSelect"] })
  .refine((g) => g.modifiers.every((m) => !m.productId || !!m.quantity), { error: "Укажите количество списания для модификатора с продуктом", path: ["modifiers"] });
export const modifierGroupUpdateSchema = z.intersection(modifierGroupSchema, z.object({ version: z.number().int().min(1) }));
export type ModifierGroupInput = z.input<typeof modifierGroupSchema>;

export const stopListSchema = z.strictObject({
  menuItemId: uuid,
  reason: z.enum(["out_of_stock", "manual"]).default("manual"),
  remainingQty: optionalDecimal,
  until: z.iso.datetime({ error: "Некорректная дата" }).nullable().optional(),
});

// ───────── Массовые изменения (чек-лист на странице «Меню») ─────────
const bulkIds = z.array(uuid).min(1, { error: "Выберите хотя бы одну запись" }).max(500, { error: "Не больше 500 записей за раз" });
export const bulkIdsSchema = z.strictObject({ ids: bulkIds });

/** Цена: percent — на ±N % (−90…+500), amount — на ±N minor; результат не меньше 0. */
export const bulkPriceChange = z.strictObject({
  mode: z.enum(["percent", "amount"]),
  value: z.number().int({ error: "Введите целое число" }).refine((v) => v !== 0, { error: "Изменение не может быть нулевым" }),
}).refine((v) => v.mode !== "percent" || (v.value >= -90 && v.value <= 500), { error: "Процент — от −90 до 500", path: ["value"] });

export const menuItemsBulkSchema = z
  .strictObject({
    ids: bulkIds,
    categoryId: uuid.optional(),
    isActive: z.boolean().optional(),
    isVisiblePublic: z.boolean().optional(),
    allowDiscount: z.boolean().optional(),
    price: bulkPriceChange.optional(),
  })
  .refine((v) => [v.categoryId, v.isActive, v.isVisiblePublic, v.allowDiscount, v.price].some((x) => x !== undefined), { error: "Выберите, что изменить", path: ["ids"] });

export const menuCategoriesBulkSchema = z
  .strictObject({
    ids: bulkIds,
    isVisible: z.boolean().optional(),
    /** Каналы: add — добавить к текущим, remove — убрать. */
    addChannels: z.array(z.enum(CHANNELS)).max(4).optional(),
    removeChannels: z.array(z.enum(CHANNELS)).max(4).optional(),
  })
  .refine((v) => v.isVisible !== undefined || !!v.addChannels?.length || !!v.removeChannels?.length, { error: "Выберите, что изменить", path: ["ids"] });

export const stopListBulkSchema = z.strictObject({
  menuItemIds: bulkIds,
  reason: z.enum(["out_of_stock", "manual"]).default("manual"),
  remainingQty: optionalDecimal,
});
export const stopListBulkRemoveSchema = z.strictObject({ menuItemIds: bulkIds });

export const branchItemSettingSchema = z.strictObject({
  isAvailable: z.boolean(),
  priceOverride: money.nullable(),
});

// ───────── ТТК ─────────
export const recipeItemSchema = z
  .strictObject({
    productId: uuid.nullable().optional(),
    subRecipeId: uuid.nullable().optional(),
    grossQty: decimal.refine((v) => Number(v) > 0, { error: "Больше 0" }),
    unit: z.enum(["g", "kg", "ml", "l", "pcs", "portion"]),
    wastePercent: pct.default(0),
    cookLossPercent: pct.default(0),
    appliesToOrderTypes: z.array(z.enum(["dine_in", "takeaway", "delivery"])).default([]),
  })
  .refine((i) => !!i.productId !== !!i.subRecipeId, { error: "Выберите продукт или полуфабрикат", path: ["productId"] });

export const recipeDraftSchema = z.strictObject({
  outputQty: decimal.refine((v) => Number(v) > 0, { error: "Больше 0" }).default("1"),
  outputUnit: z.enum(["portion", "g", "ml", "pcs"]).default("portion"),
  instructions: trimmed(0, 5000).nullable().optional(),
  cookingTimeMin: z.number().int().min(0).max(1440).nullable().optional(),
  items: z.array(recipeItemSchema).max(100),
});
export type RecipeDraftInput = z.input<typeof recipeDraftSchema>;

/** Копирование ТТК (FR-REC-008): из источника в черновики целей; factor — коэффициент граммовок (S → L = 1.5). */
const recipeCopyOwner = { ownerType: z.enum(["menu_item", "variant", "modifier"]), ownerId: uuid };
export const recipeCopySchema = z.strictObject({
  from: z.strictObject(recipeCopyOwner),
  targets: z
    .array(z.strictObject({ ...recipeCopyOwner, factor: z.number().min(0.01, { error: "Коэффициент от 0.01 до 100" }).max(100, { error: "Коэффициент от 0.01 до 100" }).default(1) }))
    .min(1, { error: "Выберите, куда копировать" })
    .max(50),
});
export type RecipeCopyInput = z.input<typeof recipeCopySchema>;

export const recipePublishSchema = z.strictObject({ validFrom: z.iso.datetime().nullable().optional() });

export const costingSettingsSchema = z.strictObject({
  targetFoodCostPct: z.number().min(1).max(99),
  priceRounding: z.number().int().min(0).max(100_000),
  base: z.enum(["gross", "net"]),
});

// ───────── Залы и столы ─────────
export const HALL_TYPES = ["hall", "vip", "terrace", "room", "banquet", "bar"] as const;
export const hallSchema = z.strictObject({ branchId: uuid, name: trimmed(1, 60), type: z.enum(HALL_TYPES).default("hall") });
export const hallUpdateSchema = hallSchema.omit({ branchId: true }).partial().extend({ version: z.number().int().min(1) });

export const tableSchema = z.strictObject({
  hallId: uuid,
  number: z.string().trim().min(1, { error: "Укажите номер" }).max(10),
  name: trimmed(0, 60).nullable().optional(),
  capacity: z.number().int().min(1, { error: "От 1 места" }).max(500),
  shape: z.enum(["round", "square", "rect"]).default("square"),
  posX: z.number().int().min(0).max(10000).default(0),
  posY: z.number().int().min(0).max(10000).default(0),
  width: z.number().int().min(40).max(1000).default(120),
  height: z.number().int().min(40).max(1000).default(120),
  rotation: z.number().int().min(0).max(359).default(0),
  isActive: z.boolean().default(true),
  minOrderAmount: money.nullable().optional(),
  /** Сетка плана кассы (контракт Kafe Coco: row, column, columnSpan) */
  gridRow: z.number().int().min(0).max(99).nullable().optional(),
  gridColumn: z.number().int().min(0).max(99).nullable().optional(),
  gridColumnSpan: z.number().int().min(1).max(12).default(1),
});
export const tableUpdateSchema = tableSchema.partial().extend({ version: z.number().int().min(1) });
export const TABLE_STATUSES = ["free", "occupied", "awaiting_payment", "reserved", "cleaning", "unavailable"] as const;
export const tableStatusSchema = z.strictObject({ status: z.enum(TABLE_STATUSES), version: z.number().int().min(1) });
/** Сохранение раскладки плана зала одним запросом. */
export const floorPlanSchema = z.strictObject({
  tables: z.array(z.strictObject({ id: uuid, posX: z.number().int().min(0).max(10000), posY: z.number().int().min(0).max(10000), rotation: z.number().int().min(0).max(359).optional(),
    gridRow: z.number().int().min(0).max(99).nullable().optional(), gridColumn: z.number().int().min(0).max(99).nullable().optional(), gridColumnSpan: z.number().int().min(1).max(12).optional() })).max(500),
});

export const menuTagSchema = z.strictObject({ name: trimmed(1, 40), sortOrder: z.number().int().min(0).max(10_000).optional() });
export const menuTagUpdateSchema = menuTagSchema.partial().extend({ version: z.number().int().min(1) });
